/**
 * Flood Fusion Anomaly detail panel (operator-cop-doctrine §2.7 / §4.4 / §3B.8, IMS
 * §7.2). A SCORED anomaly (carries an IMS Response Level) raised when a gauge's
 * SUSTAINED rate-of-rise clears the TSR-derived threshold AND a downstream community
 * is in reach. The act-register signal on top of the ambient Hydrometric Gauges
 * layer. Two operator actions:
 *   • Acknowledge → demotes in the queue; marker stays (§6.1).
 *   • Dismiss     → logged false positive; auto-leaves the queue.
 * It also auto-clears when the rise subsides or the gauge goes stale.
 *
 * Honesty (recon §C): there is NO official Canadian flood stage — the signal is the
 * TSR-derived rate-of-rise; water level is station-local ASSUMED datum (trend only).
 * That disclaimer is load-bearing and rendered in the panel.
 */
import { useEffect, useState, type ComponentProps } from 'react';
import {
  acknowledgeFloodAnomaly,
  dismissFloodAnomaly,
  fetchFloodAnomalyDetail,
  fetchIncidentForSource,
  type FloodAnomalyFeature,
  type FloodCommunityInReach,
  type FloodGaugeReading,
} from './api';
import { floodTypeLabel } from './floodAnomalyStyle';
import { AnomalyResponseLevelBadge } from './AnomalyResponseLevelBadge';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { IncidentWorkSurface } from './SidePanel';
import { CommunityCommsThread } from './CommunityCommsThread';
import { AnomalyAssessmentControl } from './AnomalyAssessmentControl';
import { OwnershipCoordinationBlock } from './components/OwnershipCoordinationBlock';

/** The IncidentWorkSurface callback contract, reused so the flood panel threads the
 *  same handlers the fire body does (no re-typing the feature shapes). */
type IncidentSurfaceProps = ComponentProps<typeof IncidentWorkSurface>;

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

export function FloodAnomalyBody({
  anomaly,
  onResolved,
  onFlyToIncident,
  onOpenEvacuation,
  onSelectCamera,
  onSelectAsset,
  onSelectAircraft,
  onOpenSitrep,
  onOpenConnectedAssist,
  onOpenReplay,
  onSelectMission,
  aarReplayActive = false,
}: {
  anomaly: FloodAnomalyFeature;
  /** Called after acknowledge/dismiss so the parent refreshes the queue + selection. */
  onResolved?: (outcome: 'acknowledged' | 'dismissed') => void;
  /** Fly the camera to a community-in-reach centroid. */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** Open the incident-scoped Evacuation Logistics surface (§4.4). */
  onOpenEvacuation?: (incidentId: string) => void;
  /** #425 — IncidentWorkSurface handlers, threaded from the SidePanel body switch so a
   *  declared flood incident is worked from the same surface a fire is. */
  onSelectCamera?: IncidentSurfaceProps['onSelectCamera'];
  onSelectAsset?: IncidentSurfaceProps['onSelectAsset'];
  onSelectAircraft?: IncidentSurfaceProps['onSelectAircraft'];
  onOpenSitrep?: IncidentSurfaceProps['onOpenSitrep'];
  onOpenConnectedAssist?: IncidentSurfaceProps['onOpenConnectedAssist'];
  onOpenReplay?: IncidentSurfaceProps['onOpenReplay'];
  onSelectMission?: IncidentSurfaceProps['onSelectMission'];
  aarReplayActive?: boolean;
}): JSX.Element {
  const p = anomaly.properties;
  const [lon, lat] = anomaly.geometry.coordinates;
  const [busy, setBusy] = useState<'ack' | 'dismiss' | null>(null);
  const [reason, setReason] = useState('');
  const [communities, setCommunities] = useState<FloodCommunityInReach[]>([]);
  const [readings, setReadings] = useState<FloodGaugeReading[]>([]);
  const [evacIncidentId, setEvacIncidentId] = useState<string | null>(null);
  const [evacIncidentNumber, setEvacIncidentNumber] = useState<string | null>(null);
  // #425 — the incident promoted FROM this flood anomaly (source_record link), if any.
  // Mirrors FireBody (SidePanel.tsx): resolve the existing incident so the work surface
  // opens straight to management; null ⇒ the one-click Declare affordance.
  const [incidentId, setIncidentId] = useState<string | null>(null);

  useEffect(() => {
    logAction('view_flood_anomaly_detail', { anomaly_id: anomaly.id });
    const ctl = new AbortController();
    void (async () => {
      try {
        const detail = await fetchFloodAnomalyDetail(anomaly.id, ctl.signal);
        setCommunities(detail.properties.communities_in_reach_detail ?? []);
        setReadings(detail.properties.gauge_readings ?? []);
        setEvacIncidentId(detail.properties.evacuation_incident_id ?? null);
        setEvacIncidentNumber(detail.properties.evacuation_incident_number ?? null);
      } catch {
        /* detail enrichment is best-effort */
      }
    })();
    return () => ctl.abort();
  }, [anomaly.id]);

  // #425 — resolve the incident promoted from THIS anomaly (idempotent on the source).
  useEffect(() => {
    setIncidentId(null);
    const ctl = new AbortController();
    void fetchIncidentForSource('flood_anomaly', anomaly.id, ctl.signal)
      .then((inc) => setIncidentId(inc?.id ?? null))
      .catch(() => setIncidentId(null));
    return () => ctl.abort();
  }, [anomaly.id]);

  const acknowledge = async (): Promise<void> => {
    setBusy('ack');
    try {
      const sessionId = getSessionId();
      logAction('acknowledge_flood_anomaly', { anomaly_id: anomaly.id });
      await acknowledgeFloodAnomaly(anomaly.id, sessionId);
      showToast('Flood anomaly acknowledged — demoted in the queue; marker stays.', {
        variant: 'success',
      });
      onResolved?.('acknowledged');
    } catch {
      showToast('Could not acknowledge the anomaly.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      const sessionId = getSessionId();
      logAction('dismiss_flood_anomaly', { anomaly_id: anomaly.id, reason: reason || null });
      await dismissFloodAnomaly(anomaly.id, sessionId, reason || undefined);
      showToast('Flood anomaly dismissed — logged as a false positive. Left the queue.', {
        variant: 'success',
      });
      onResolved?.('dismissed');
    } catch {
      showToast('Could not dismiss the anomaly.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const latest = readings[0];

  return (
    <div className="px-4 py-4 space-y-4" data-testid="flood-anomaly-body">
      <div className="border-l-2 border-blue-400 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          🌊 Flood anomaly · {floodTypeLabel(p.anomaly_type)}
        </p>
        <h2 className="text-base font-semibold">{p.river ?? 'River gauge'} rising</h2>
        <div className="mt-1.5">
          {/* #445-ws2 — the operative (IC-set) Response Level over the annotated system-derived. */}
          <AnomalyResponseLevelBadge
            derived={p.response_level}
            operatorSet={p.operator_response_level ?? null}
            assessedBy={p.operator_rl_by ?? null}
            reason={p.operator_rl_reason ?? null}
            testidPrefix="flood-anomaly-response-level"
            extra={
              <>
                {p.status === 'acknowledged' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                    acknowledged
                  </span>
                )}
                {p.gauge_sparse && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300"
                    title="The nearest gauge weakly covers a served community (recon §B)."
                  >
                    gauge-sparse
                  </span>
                )}
              </>
            }
          />
        </div>
      </div>

      {/* Ownership-coordination — the cross-agency "who holds command + how to reach them" read. */}
      {p.owned_incident && <OwnershipCoordinationBlock owned={p.owned_incident} />}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Gauge reading</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Station</dt>
          <dd className="font-mono text-xs">
            {p.station_number}
            {p.station_name ? ` · ${p.station_name}` : ''}
          </dd>
          <dt className="text-slate-400">Rate of rise</dt>
          <dd className="font-mono font-semibold">{p.rate_of_rise_cm_per_hr.toFixed(1)} cm/hr</dd>
          <dt className="text-slate-400">Water level</dt>
          <dd className="font-mono">{p.water_level_m != null ? `${p.water_level_m.toFixed(3)} m` : '—'}</dd>
          <dt className="text-slate-400">Discharge</dt>
          <dd className="font-mono">{p.discharge_cms != null ? `${p.discharge_cms.toLocaleString()} m³/s` : '—'}</dd>
          <dt className="text-slate-400">Latest reading</dt>
          <dd>{latest ? agoLabel(latest.observed_at) : agoLabel(p.last_seen_at)}</dd>
          <dt className="text-slate-400">First detected</dt>
          <dd>{agoLabel(p.first_detected_at)}</dd>
        </dl>
      </section>

      {readings.length > 1 && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            Recent readings
          </h3>
          <ul className="text-[11px] font-mono space-y-0.5 max-h-32 overflow-y-auto">
            {readings.slice(0, 8).map((r, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-slate-400">
                <span>{agoLabel(r.observed_at)}</span>
                <span>{r.water_level_m != null ? `${r.water_level_m.toFixed(3)} m` : '—'}</span>
                <span
                  className={
                    r.rate_of_rise_cm_per_hr != null && r.rate_of_rise_cm_per_hr > 0
                      ? 'text-blue-300'
                      : 'text-slate-500'
                  }
                >
                  {r.rate_of_rise_cm_per_hr != null ? `${r.rate_of_rise_cm_per_hr.toFixed(1)}` : '—'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="border-t border-slate-800 pt-3">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Communities in reach{communities.length ? ` · ${communities.length}` : ''}
        </h3>
        {communities.length ? (
          <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
            {communities.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="truncate text-left hover:text-blue-300"
                  onClick={() => {
                    logAction('fly_to_community', { community_id: c.id, anomaly_id: anomaly.id });
                    onFlyToIncident?.(c.lat, c.lon);
                  }}
                  title="Fly to community"
                >
                  ✈ {c.name}
                </button>
                <span className="font-mono text-xs text-slate-400 shrink-0">
                  {c.population != null ? `${c.population.toLocaleString()} pop · ` : ''}
                  {c.vulnerable_population != null ? `${c.vulnerable_population} vuln` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-slate-500">No tracked communities within reach.</p>
        )}
        {evacIncidentId && (
          <button
            type="button"
            onClick={() => {
              logAction('view_evacuation_logistics', {
                incident_id: evacIncidentId,
                from: 'flood_anomaly',
              });
              onOpenEvacuation?.(evacIncidentId);
            }}
            className="mt-2 w-full rounded bg-blue-700 hover:bg-blue-600 px-3 py-2 text-sm font-medium"
          >
            Open Evacuation Logistics{evacIncidentNumber ? ` · ${evacIncidentNumber}` : ''}
          </button>
        )}
      </section>

      {/* #420a — coordinate with the affected community IN the COP (the §E sim G2-closer),
          incident-free. Bound to the PRIMARY in-reach community; only shown when the
          flood actually reaches a tracked community to coordinate with. */}
      {communities[0] && (
        <CommunityCommsThread
          communityId={communities[0].id}
          boundLabel={communities[0].name}
        />
      )}

      {p.cap_event && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            Corroborating warning
          </h3>
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-slate-400">Warning</dt>
            <dd>{p.cap_event}</dd>
            <dt className="text-slate-400">Severity</dt>
            <dd>{p.cap_severity ?? '—'}</dd>
          </dl>
          <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
            ECCC CAP carries rainfall / coastal-flood precursors only — river-flood
            warnings are provincial (SWMC) and not in CAP. This is a boost, not the trigger.
          </p>
        </section>
      )}

      {/* #425 — declare-from-any-hazard: the SAME IncidentWorkSurface the fire body
          mounts, anchored on this flood anomaly. Unowned ⇒ one-click Declare; once
          owned it opens to the incident management section (op-periods, resources,
          drones, SITREP, evac) — flood is now a tracked incident, not a dead-end alert. */}
      <section className="border-t border-slate-800 pt-3" data-testid="flood-incident-surface">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Incident</h3>
        <IncidentWorkSurface
          source={{
            recordType: 'flood_anomaly',
            recordId: anomaly.id,
            lat,
            lon,
            aoLabel: 'this flood anomaly',
            declareName: `Flood anomaly — ${p.river ?? p.station_name ?? p.station_number} (${lat.toFixed(3)}, ${lon.toFixed(3)})`,
            declareHazardType: 'flood',
          }}
          incidentId={incidentId}
          lat={lat}
          lon={lon}
          cameraTuning="all-nearby"
          onIncidentDeclared={(inc) => setIncidentId(inc.id)}
          onSelectCamera={onSelectCamera}
          onSelectAsset={onSelectAsset}
          onSelectAircraft={onSelectAircraft}
          onOpenSitrep={onOpenSitrep}
          onOpenConnectedAssist={onOpenConnectedAssist}
          onOpenEvacuation={onOpenEvacuation}
          onOpenReplay={onOpenReplay}
          onSelectMission={onSelectMission}
          replayActive={aarReplayActive}
        />
      </section>

      <section className="border-t border-slate-800 pt-3 space-y-2">
        {/* #331 — the REVERSIBLE assessment axis. Highest-value on flood: a false positive
            here mirrors the source status to 'dismissed' so the #249 re-alarm override
            re-raises it if the gauge escalates, AND the inline Reopen makes a mis-clicked
            dismiss recoverable in-COP (the legacy Acknowledge/Dismiss buttons below stay
            for now — the full dismiss-unification is the named next-slice). */}
        <AnomalyAssessmentControl
          source="flood"
          sourceId={anomaly.id}
          initialAssessment={p.assessment ?? 'unconfirmed'}
        />
        <label className="block text-[11px] text-slate-400" htmlFor="flood-dismiss-reason">
          Dismiss reason (optional)
        </label>
        <input
          id="flood-dismiss-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. gauge fault / normal freshet / not evac-relevant"
          className="w-full rounded bg-slate-800 border border-slate-700 px-2 py-1 text-sm"
        />
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy !== null || p.status === 'acknowledged'}
            onClick={() => void acknowledge()}
            className="flex-1 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'ack' ? 'Acknowledging…' : 'Acknowledge'}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void dismiss()}
            className="flex-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <span className="text-amber-400">No official Canadian flood stage exists.</span>{' '}
          The signal is a TSR-derived rate-of-rise; water level is station-local (ASSUMED
          datum, trend only). Acknowledge demotes it (marker stays); dismiss logs a false
          positive. The anomaly also auto-clears when the rise subsides. Both actions are
          audited (no hard delete).
        </p>
      </section>

    </div>
  );
}
